import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import CommonPagination from "../../components/widgets/common_pagination";
import { CommonTextField } from "../../components/widgets/common_textField";
import CommonButton from "../../components/widgets/common_button";
import JobsService from "../../service/jobs.service";
import Toaster from "../../components/common/toaster";
import { Eye, Trash2, X, Briefcase, MapPin } from "lucide-react";

const Jobs = () => {
  const navigate = useNavigate();

  // Initialize state with potential restoration from localStorage
  const getInitialState = () => {
    const savedPaginationState = localStorage.getItem("jobsPaginationState");
    if (savedPaginationState) {
      try {
        const paginationState = JSON.parse(savedPaginationState);
        const isRecent =
          Date.now() - paginationState.timestamp < 30 * 60 * 1000;
        if (isRecent) {
          // Clear the saved state after using it to prevent reuse
          localStorage.removeItem("jobsPaginationState");
          return {
            page: paginationState.page || 1,
            size: paginationState.size || 8,
            search: paginationState.search || "",
            searchInput: paginationState.search || "",
            statusFilter: paginationState.statusFilter || "all",
          };
        } else {
          // Clear old state
          localStorage.removeItem("jobsPaginationState");
        }
      } catch (error) {
        // Clear corrupted state
        localStorage.removeItem("jobsPaginationState");
      }
    }
    return {
      page: 1,
      size: 8,
      search: "",
      searchInput: "",
      statusFilter: "all",
    };
  };

  const initialState = getInitialState();
  const [page, setPage] = useState(initialState.page);
  const [size, setSize] = useState(initialState.size);
  const [search, setSearch] = useState(initialState.search);
  const [searchInput, setSearchInput] = useState(initialState.searchInput);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalJobs, setTotalJobs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState(initialState.statusFilter);
  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
  const [selectedJobCategories, setSelectedJobCategories] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [deletingJob, setDeletingJob] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    totalActiveJobs: 0,
    totalPausedJobs: 0,
  });
  const isMountedRef = useRef(true);
  const isInitialLoadRef = useRef(true);

  const capitalize = (text) => {
    if (typeof text !== "string" || text.length === 0) return "-";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const imagePlaceholder =
    "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

  // Fetch dashboard metrics
  const fetchDashboardMetrics = useCallback(async () => {
    try {
      // Use Promise.all to fetch both metrics simultaneously
      const [activeJobsResponse, pausedJobsResponse] = await Promise.all([
        JobsService.getJobs({
          page: 1,
          limit: 1,
          status: "active",
        }),
        JobsService.getJobs({
          page: 1,
          limit: 1,
          status: "pause",
        }),
      ]);

      const totalActiveJobs = activeJobsResponse?.data?.total || 0;
      const totalPausedJobs = pausedJobsResponse?.data?.total || 0;

      setDashboardMetrics({
        totalActiveJobs,
        totalPausedJobs,
      });
    } catch (error) {
      console.error("Failed to fetch dashboard metrics:", error);
    }
  }, []);

  // Fetch jobs from API
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: size,
      };

      // Only add search parameter if it's not empty
      if (search && search.trim() !== "") {
        params.search = search.trim();
      }

      // Add status filter
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

     
      const response = await JobsService.getJobs(params);
     

      if (response?.data?.data) {
        setJobs(response.data.data);
        setTotalJobs(response.data.total || response.data.data.length);
        setTotalPages(
          Math.ceil((response.data.total || response.data.data.length) / size)
        );
      }
    } catch (error) {
      Toaster(
        "error",
        error?.response?.data?.message || "Failed to fetch jobs"
      );
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [page, size, search, statusFilter]);

  // Debounced search function
  const debouncedSearch = useCallback((searchTerm) => {
    setSearch(searchTerm);
    setPage(1); // Reset to first page when searching
  }, []);

  // Debounced search effect
  useEffect(() => {
    // Skip debounced search during initial load to prevent page reset
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      debouncedSearch(searchInput);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, debouncedSearch]);

  // Load jobs on component mount and when page/size/statusFilter changes
  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, search, statusFilter]);

  // Load dashboard metrics only once on component mount
  useEffect(() => {
    fetchDashboardMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs only once

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDataSize = (value) => {
    setSize(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    localStorage.setItem("jobStatusFilter", value); // Save to localStorage
    setPage(1); // Reset to first page when filter changes
  };

  const handleViewCategories = (categories) => {
    setSelectedJobCategories(categories || []);
    setCategoriesDialogOpen(true);
  };

  const handleDeleteJob = (job) => {
    setJobToDelete(job);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteJob = async () => {
    if (!jobToDelete) return;

    setDeletingJob(true);
    try {
      await JobsService.deleteJob(jobToDelete._id);

      // Remove the job from the local state
      setJobs((prevJobs) =>
        prevJobs.filter((job) => job._id !== jobToDelete._id)
      );

      // Update total count
      setTotalJobs((prev) => prev - 1);

      Toaster("success", "Job deleted successfully");
    } catch (error) {
      Toaster(
        "error",
        error?.response?.data?.message || "Failed to delete job"
      );
    } finally {
      setDeletingJob(false);
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  return (
    <div className="space-y-1">
      {/* Dashboard Metrics */}
      <div className="pb-4 flex-shrink-0">
        <div className="flex justify-start gap-6">
          {/* Total Active Jobs */}
          <Card className="p-6 rounded-lg shadow-sm h-24 w-64">
            <div className="flex items-center gap-4 h-full">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Active Jobs</p>
                <p className="text-3xl font-bold text-green-600">
                  {dashboardMetrics.totalActiveJobs}
                </p>
              </div>
            </div>
          </Card>

          {/* Total Paused Jobs */}
          <Card className="p-6 rounded-lg shadow-sm h-24 w-64">
            <div className="flex items-center gap-4 h-full">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Paused Jobs</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {dashboardMetrics.totalPausedJobs}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <Card className="p-10 grid gap-6">
        <div className="flex items-center justify-between w-full gap-4">
          <h3 className="text-xl tab:text-2xl font-bold">Jobs List</h3>

          <div className="flex items-center gap-4 w-full max-w-md lg:w-auto">
            <div className="relative flex-1">
              <CommonTextField
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search jobs title..."
                className="w-full pr-10"
              />
              {searchInput && (
                <button
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                    setPage(1);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter dropdown */}
            <div className="w-48">
              <Select
                value={statusFilter}
                onValueChange={handleStatusFilterChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Jobs</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pause">Pause</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[600px]">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="text-center text-black/50 font-medium text-xl">
                Loading Ad...
              </div>
            </div>
          ) : jobs.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {jobs.map((item, index) => (
                <Card
                  key={item._id || index}
                  className="overflow-hidden hover:shadow-lg transition-shadow duration-200 h-[450px] flex flex-col"
                >
                  {/* Job Image - Dynamic size based on content */}
                  {(() => {
                    const hasTitle =
                      item?.title &&
                      item.title.trim() !== "" &&
                      item.title !== "-";
                    const hasDescription =
                      item?.description &&
                      item.description.trim() !== "" &&
                      item.description !== "-";
                    const hasBothContent = hasTitle && hasDescription;

                    return (
                      <div
                        className={`w-full overflow-hidden ${
                          hasBothContent ? "h-90" : "h-[1500px]"
                        }`}
                      >
                        <img
                          src={item.jobImg || imagePlaceholder}
                          alt={capitalize(item?.title)}
                          className={`w-full object-cover ${
                            hasBothContent ? "h-150" : "h-[800px]"
                          }`}
                        />
                      </div>
                    );
                  })()}

                  {/* Card Content */}
                  {(() => {
                    const hasTitle =
                      item?.title &&
                      item.title.trim() !== "" &&
                      item.title !== "-";
                    const hasDescription =
                      item?.description &&
                      item.description.trim() !== "" &&
                      item.description !== "-";
                    const hasBothContent = hasTitle && hasDescription;

                    return (
                      <div
                        className={`flex flex-col h-full ${
                          hasBothContent ? "p-4" : "p-2"
                        }`}
                      >
                        {/* User Profile Section */}
                        <div
                          className={`flex items-center gap-3 ${
                            hasBothContent ? "mb-3" : "mb-2"
                          }`}
                        >
                          <div className="h-10 w-10 overflow-hidden rounded-full flex-shrink-0">
                            <img
                              src={
                                item.createdBy?.profilePic || imagePlaceholder
                              }
                              alt="Profile"
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">
                              {capitalize(item?.createdBy?.firstName)}
                            </p>
                            <p className="text-xs text-gray-500">Job Creator</p>
                          </div>
                        </div>

                        {/* Job Title - Only show if title exists */}
                        {item?.title &&
                          item.title.trim() !== "" &&
                          item.title !== "-" && (
                            <div className="mb-2">
                              <div className="flex items-center gap-2">
                                <label className="font-medium text-lg ">
                                  Title:
                                </label>
                                <h3 className="font-bold text-base text-gray-700 line-clamp-2">
                                  {capitalize(item?.title)}
                                </h3>
                              </div>
                            </div>
                          )}

                        {/* Job Description - Only show if description exists */}
                        {item?.description &&
                          item.description.trim() !== "" &&
                          item.description !== "-" && (
                            <div className="flex-1 mb-3">
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {capitalize(item?.description)}
                              </p>
                            </div>
                          )}

                        {/* Location - Only show if title and description exist */}
                        {(() => {
                          const hasTitle =
                            item?.title &&
                            item.title.trim() !== "" &&
                            item.title !== "-";
                          const hasDescription =
                            item?.description &&
                            item.description.trim() !== "" &&
                            item.description !== "-";
                          const hasBothContent = hasTitle && hasDescription;

                          return (
                            hasBothContent && (
                              <div className="mb-4">
                                <div className="flex items-center gap-2 bg-purple-50 rounded-lg p-2">
                                  <MapPin className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                  <p className="text-sm text-gray-700 line-clamp-1">
                                    {capitalize(item?.location)}
                                  </p>
                                </div>
                              </div>
                            )
                          );
                        })()}

                        {/* Actions - Fixed at bottom */}
                        {(() => {
                          const hasTitle =
                            item?.title &&
                            item.title.trim() !== "" &&
                            item.title !== "-";
                          const hasDescription =
                            item?.description &&
                            item.description.trim() !== "" &&
                            item.description !== "-";
                          const hasBothContent = hasTitle && hasDescription;

                          return (
                            <div
                              className={`flex items-center mt-auto ${
                                hasBothContent
                                  ? "justify-end"
                                  : "justify-between"
                              }`}
                            >
                              {/* Location on left side when no title/description */}
                              {!hasBothContent && (
                                <div className="flex items-center gap-2 bg-purple-50 rounded-lg p-2">
                                  <MapPin className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                  <p className="text-sm text-gray-700 line-clamp-1">
                                    {capitalize(item?.location)}
                                  </p>
                                </div>
                              )}

                              {/* Action buttons */}
                              <div className="flex items-center gap-2">
                                <CommonButton
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    // Save current pagination state to localStorage
                                    const paginationState = {
                                      page,
                                      size,
                                      search,
                                      statusFilter,
                                      timestamp: Date.now(),
                                    };
                                    localStorage.setItem(
                                      "jobsPaginationState",
                                      JSON.stringify(paginationState)
                                    );

                                    navigate(`/jobs/${item._id}`, {
                                      state: {
                                        job: item,
                                        from: "/jobs",
                                        currentFilter: statusFilter,
                                      },
                                    });
                                  }}
                                  className="flex items-center p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Eye className="h-4 w-4" />
                                </CommonButton>

                                <CommonButton
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteJob(item)}
                                  className="flex items-center p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </CommonButton>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </Card>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-40">
              <div className="text-center text-black/50 font-medium text-xl">
                No AD found
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between max-md:flex-col gap-4">
          {totalJobs > size && (
            <CommonPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(newPage) => setPage(newPage)}
              pageSize={size}
              onPageSizeChange={handleDataSize}
              pageSizeOptions={["8", "10", "25", "50", "100"]}
              className=""
            />
          )}
        </div>

        {/* Categories Dialog */}
        <Dialog
          open={categoriesDialogOpen}
          onOpenChange={setCategoriesDialogOpen}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Job Categories</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {selectedJobCategories.length > 0 ? (
                <div className="space-y-2">
                  {selectedJobCategories.map((category, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-blue-600 font-medium">•</span>
                      <span className="text-sm">{category}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No categories available</p>
              )}
            </div>
            <DialogFooter>
              <CommonButton
                variant="outline"
                onClick={() => setCategoriesDialogOpen(false)}
              >
                Close
              </CommonButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-500" />
                Delete Job
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this job?
              </p>
              {jobToDelete && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">
                    {capitalize(jobToDelete?.title)}
                  </p>
                  <p className="text-sm text-gray-600">
                    Created by: {capitalize(jobToDelete?.createdBy?.firstName)}
                  </p>
                </div>
              )}
              <p className="text-sm text-red-600 mt-3">
                This action cannot be undone. The job will be permanently
                deleted.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <CommonButton
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setJobToDelete(null);
                }}
              >
                Cancel
              </CommonButton>
              <CommonButton
                variant="destructive"
                onClick={confirmDeleteJob}
                disabled={deletingJob}
              >
                {deletingJob ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  "Delete Job"
                )}
              </CommonButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
};

export default Jobs;
